import { yupResolver } from '@hookform/resolvers/yup'
import {
  MyAsyncDropdown,
  MyButton,
  MyCalendar,
  MyCheckbox,
  MyConfirmModalWithChildren,
  MyDropzone,
  MyTextArea,
  myToaster,
} from '@interstellar-component'
import { Calendar, LinkBroken02 } from '@untitled-ui/icons-react'
import { format } from 'date-fns'
import PropTypes from 'prop-types'
import { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import ReportEnquiryService from '../../../ReportEnquiry/service'
import revokeReportSchema from './revokeReportSchema'

// Build today's date as "YYYY-MM-DD" in the operator's local timezone. The
// natural "toISOString().slice(0,10)" path silently converts to UTC, which
// shifts the date one day for clients near the GMT boundaries — the revoke
// must reflect the operator's wall clock, not the server's.
function todayInClientTz() {
  const d = new Date()
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

// Parse a "YYYY-MM-DD" (DATEONLY) string to a LOCAL-midnight Date. `new
// Date("YYYY-MM-DD")` parses as UTC and can shift the day across the GMT
// boundary — we build from local parts so the calendar's maxDate lands on the
// intended calendar day. Returns undefined for empty/invalid input.
function parseYmdLocal(value) {
  if (!value) return undefined
  const m = String(value).slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!m) {
    const d = new Date(value)
    return Number.isNaN(d.getTime()) ? undefined : d
  }
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
}

// Tomorrow at local midnight — the earliest selectable custom revoke date
// (today/past disabled; use "Immediately" for an instant revoke).
function tomorrowLocal() {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  d.setDate(d.getDate() + 1)
  return d
}

// Earliest selectable custom revoke date, clamped so it never lands AFTER the
// consent expiry. Normally tomorrow, but when consent expires today (or tomorrow)
// an unclamped tomorrow would make maxDate < minDate and react-date-range then
// disables every single day — the "custom date can't be picked" bug. Falling back
// to the expiry day keeps at least that day selectable.
function revokeMinDate(consentExpiry) {
  const tomorrow = tomorrowLocal()
  const max = parseYmdLocal(consentExpiry)
  return max && max < tomorrow ? max : tomorrow
}

export default function RevokeReportForm({ open, onClose, onRevoke, consentExpiry }) {
  const [isUploading, setIsUploading] = useState(false)
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
    setValue,
    clearErrors,
    trigger,
    watch,
  } = useForm({
    resolver: yupResolver(revokeReportSchema),
    defaultValues: {
      reason: null,
      evidence: [],
      notes: '',
      effectiveDate: null,
      customDate: null,
      consent: false,
    },
    mode: 'onChange',
  })

  useEffect(() => {
    if (open) {
      reset()
    }
  }, [open, reset])

  // MyAsyncDropdown isn't auto-wired to react-hook-form Controller — it just
  // renders MUI's Autocomplete with whatever `value`/`onChange` we pass. We
  // mirror the working pattern from NewLevelSlider.jsx: subscribe to the
  // field via watch() and write back through setValue() on change.
  const reasonValue = watch('reason')
  const effectiveDateValue = watch('effectiveDate')
  const customDateValue = watch('customDate')
  const isCustomDate = effectiveDateValue?.value === 'custom_date'

  const onSubmit = useCallback(
    async (data) => {
      try {
        // Upload evidence files (if any) sequentially through the backend's
        // multipart endpoint, then assemble the JSON revoke payload. Errors
        // here surface as a toast and the modal stays open so the operator
        // can retry without re-typing the form.
        let evidenceUrls = []
        const files = Array.isArray(data.evidence) ? data.evidence : []
        if (files.length) {
          setIsUploading(true)
          const uploaded = []
          for (const file of files) {
            const fd = new FormData()
            fd.append('file', file)
            const res = await ReportEnquiryService.uploadRevokeEvidence(fd)
            const url = res?.data?.url || res?.url
            if (!url) throw new Error('Upload returned no URL')
            uploaded.push(url)
          }
          evidenceUrls = uploaded
          setIsUploading(false)
        }

        // effective_date carries the operator's pick for custom_date; for
        // immediately it's today, and for on_next_cycle it's sent but ignored
        // (the backend computes the cycle boundary). Custom dates are formatted
        // from local parts to avoid the UTC day-shift.
        const isCustom = data.effectiveDate?.value === 'custom_date'
        const effectiveDateStr =
          isCustom && data.customDate
            ? format(data.customDate, 'yyyy-MM-dd')
            : todayInClientTz()

        const payload = {
          reason: data.reason?.value,
          notes: data.notes,
          effective_type: data.effectiveDate?.value, // "immediately" | "on_next_cycle" | "custom_date"
          effective_date: effectiveDateStr,
          evidence_urls: evidenceUrls,
        }

        await onRevoke?.(payload)
        onClose()
      } catch (err) {
        setIsUploading(false)
        myToaster({
          message: err?.message || 'Failed to revoke consent',
          type: 'error',
        })
      }
    },
    [onRevoke, onClose]
  )

  const reasonOptions = [
    'Employee resigned',
    'Employee contract ended',
    'Consent withdrawn',
    'Incorrect subject data',
    'Duplicate monitoring',
    'Monitoring no longer required',
  ].map((label) => ({ label, value: label }))

  // "Immediately" withdraws consent now; "On next cycle" defers the lapse to
  // the next monitoring cycle (backend computes the date from repeat_every);
  // "Custom date" defers to an operator-picked date (tomorrow → consent expiry,
  // enforced by the picker below and re-checked on the backend).
  const effectiveOptions = [
    { label: 'Immediately', value: 'immediately' },
    { label: 'On next cycle', value: 'on_next_cycle' },
    { label: 'Custom date', value: 'custom_date' },
  ]

  const mockAsync = (options) => (params) =>
    Promise.resolve({
      loading: false,
      data: options.filter((o) =>
        o.label.toLowerCase().includes(params?.search?.toLowerCase() || '')
      ),
    })

  return (
    <MyConfirmModalWithChildren
      open={open}
      onClose={onClose}
      onConfirm={handleSubmit(onSubmit)}
      forceBlur
      title="Revoke form"
      message="Stop future credit monitoring enquiries for this individual."
      icon={<LinkBroken02 className="text-error/600" />}
      bgColor="bg-error/100"
      negativeActionWord="Cancel"
      positiveActionWord={
        <MyButton
          expanded
          color="error"
          variant="outlined"
          size="lg"
          onClick={handleSubmit(onSubmit)}
          disabled={isSubmitting || isUploading}
        >
          <span className="text-sm-semibold">
            {isUploading ? 'Uploading…' : isSubmitting ? 'Revoking…' : 'Revoke'}
          </span>
        </MyButton>
      }
    >
      <div className="flex flex-col gap-3">
        {/* Reason for revocation */}
        <div className="flex flex-col gap-1.5">
          <label className="text-sm-medium text-gray-light/700">
            Reason for revocation <span className="text-brand/900">*</span>
          </label>
          <MyAsyncDropdown
            name="reason"
            control={control}
            trigger={trigger}
            value={reasonValue}
            placeholder="Select reason"
            asyncFunction={mockAsync(reasonOptions)}
            error={errors?.reason?.message}
            getOptionLabel={(o) => o?.label ?? ''}
            isOptionEqualToValue={(a, b) => a?.value === b?.value}
            onChange={(_e, v) =>
              setValue('reason', v, { shouldValidate: true, shouldDirty: true })
            }
          />
        </div>

        {/* Supporting evidence */}
        <div className="flex flex-col gap-1.5">
          <label className="text-sm-medium text-gray-light/700">
            Supporting evidence (optional)
          </label>
          <MyDropzone
            accept={['.svg', '.png', '.jpg', '.gif']}
            maxSize={800 * 400} // based on Figma suggestion
            onChange={(files) => setValue('evidence', files)}
          />
        </div>

        {/* Additional notes */}
        <div className="flex flex-col gap-1.5">
          <label className="text-sm-medium text-gray-light/700">
            Additional notes <span className="text-brand/900">*</span>
          </label>
          <MyTextArea
            name="notes"
            control={control}
            placeholder="Enter a notes..."
            errors={errors?.notes?.message}
            rows={4}
          />
        </div>

        {/* Revocation effective */}
        <div className="flex flex-col gap-1.5">
          <label className="text-sm-medium text-gray-light/700">
            Revocation effective <span className="text-brand/900">*</span>
          </label>
          <MyAsyncDropdown
            name="effectiveDate"
            control={control}
            trigger={trigger}
            value={effectiveDateValue}
            placeholder="Select option"
            asyncFunction={mockAsync(effectiveOptions)}
            error={errors?.effectiveDate?.message}
            getOptionLabel={(o) => o?.label ?? ''}
            isOptionEqualToValue={(a, b) => a?.value === b?.value}
            onChange={(_e, v) => {
              setValue('effectiveDate', v, { shouldValidate: true, shouldDirty: true })
              // Leaving "Custom date" → drop the picked date + any pending
              // error so a stale custom-date message never lingers under
              // another option.
              if (v?.value !== 'custom_date') {
                setValue('customDate', null, { shouldValidate: false })
                clearErrors('customDate')
              }
            }}
          />
        </div>

        {/* Custom date picker — only when "Custom date" is selected. Bounded to
            [tomorrow, consent expiry]: a revoke can defer but never extend
            consent past its current expiry. */}
        {isCustomDate && (
          <div className="flex flex-col gap-1.5">
            <label className="text-sm-medium text-gray-light/700">
              Effective date <span className="text-brand/900">*</span>
            </label>
            <MyCalendar
              value={customDateValue}
              minDate={revokeMinDate(consentExpiry)}
              maxDate={parseYmdLocal(consentExpiry)}
              onChange={(d) =>
                setValue('customDate', d, { shouldValidate: true, shouldDirty: true })
              }
              target={(_open, show) => (
                <MyButton
                  type="button"
                  color="secondary"
                  variant="outlined"
                  size="md"
                  expanded
                  onClick={show}
                  customClassname={
                    errors?.customDate
                      ? '!border-error/300 !ring-1 !ring-error/300'
                      : 'hover:!ring-2 hover:!ring-brand/400'
                  }
                >
                  <div className="flex w-full items-center gap-3 text-left">
                    <Calendar className="size-4 shrink-0 text-gray-400" />
                    <span
                      className={
                        customDateValue
                          ? 'text-sm-medium text-gray-900'
                          : 'text-md-regular text-gray-light/500'
                      }
                    >
                      {customDateValue ? format(customDateValue, 'MMM d, yyyy') : 'Select date'}
                    </span>
                  </div>
                </MyButton>
              )}
            />
            <p className="text-sm-regular text-gray-light/600">
              Consent stays active until this date, then becomes invalid.
            </p>
            {errors?.customDate?.message && (
              <p className="text-sm text-error/600">{errors.customDate.message}</p>
            )}
          </div>
        )}

        {/* Consent Checkbox */}
        <div className="flex items-start gap-3">
          <MyCheckbox name="consent" control={control} isError={!!errors?.consent} value />
          <p className="text-sm-regular text-gray-light/700">
            I agree by revoking this monitoring will mark the existing consent as no longer valid.
            Any future credit bureau enquiries will require a new consent from the individual.
          </p>
        </div>
        {errors?.consent?.message && (
          <p className="text-sm text-error/600 -mt-3">{errors.consent.message}</p>
        )}
      </div>
    </MyConfirmModalWithChildren>
  )
}

RevokeReportForm.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onRevoke: PropTypes.func,
  // Current consent_expiry (YYYY-MM-DD) — caps the custom-date picker so a
  // revoke can never extend consent. Null/undefined ⇒ no upper bound.
  consentExpiry: PropTypes.string,
}

RevokeReportForm.defaultProps = {
  onRevoke: undefined,
  consentExpiry: undefined,
}
